import {
  DEFAULT_CHARACTER_SCALE_MULTIPLIER,
  DEFAULT_SCENE_CHARACTER_SCALE,
  normalizeCharacterScale,
  normalizeCharacterScaleMultiplier,
} from '../../game/avatarScale';
import { normalizeWorldAnnotations } from '../../lib/worldAnnotations';
import { cloneMovementConfig, normalizeMovementConfig } from '../../lib/npcMovement';
import { MAP_SIZE } from '../../game/worldConstants';
import {
  DEFAULT_CAMERA_ZOOM,
  DEFAULT_WORLD_LAYOUT,
  clampTilePoint,
  normalizeCameraZoom,
  normalizeWorldLayout,
  type WorldLayout,
} from '../../game/worldLayout';
import { cloneFallbackProviders } from '../../lib/llmConfig';
import type { SceneCompositionInput } from '../../services/firebase/scenes';
import type { LevelDesignDocument } from '../../types/levelEditor';
import type { SceneDocument, SceneNpcPlacement, SceneStatus } from '../../types/scene';

export type PlacementTool =
  | { kind: 'spawn' }
  | {
      kind: 'npc';
      source: 'system' | 'user';
      sourceNpcId: string;
      name: string;
    };

export const DEFAULT_CHARACTER_SCALE: SceneDocument['characterScale'] =
  DEFAULT_SCENE_CHARACTER_SCALE;

export const createBlankSceneDraft = (): SceneCompositionInput => ({
  name: 'Untitled Scene',
  description: '',
  tags: [],
  status: 'draft',
  levelDesignId: '',
  backgroundUrl: null,
  foregroundUrl: null,
  traversalMaskUrl: null,
  thumbnailUrl: null,
  animatedGifs: [],
  worldLayout: { ...DEFAULT_WORLD_LAYOUT },
  defaultCameraZoom: DEFAULT_CAMERA_ZOOM,
  playerSpawnTile: { x: 32, y: 32 },
  defaultZone: 'default',
  zones: [],
  signs: [],
  doors: [],
  annotations: [],
  npcPlacements: [],
  characterScale: DEFAULT_CHARACTER_SCALE,
  characterScaleMultiplier: DEFAULT_CHARACTER_SCALE_MULTIPLIER,
  ambientZoneProfile: {},
});

export const trimTags = (value: string): string[] =>
  value
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry, index, list) => entry.length >= 2 && list.indexOf(entry) === index)
    .slice(0, 10);

export const toTagString = (value: string[]): string => value.join(', ');

export const clampTile = (value: number, mapSize: number): number =>
  Math.max(0, Math.min(mapSize - 1, Math.floor(value)));

export const resolveWorldLayoutFromLevelDesign = (
  design: Pick<LevelDesignDocument, 'canvasWidth' | 'canvasHeight'>,
): WorldLayout =>
  normalizeWorldLayout({
    tileWidth: design.canvasWidth,
    tileHeight: design.canvasHeight,
  });

export const resolveLevelDesignMapSize = (
  design: Pick<LevelDesignDocument, 'canvasWidth' | 'canvasHeight'>,
): number => {
  const width = Number.isFinite(design.canvasWidth) ? Math.floor(design.canvasWidth) : MAP_SIZE;
  const height = Number.isFinite(design.canvasHeight) ? Math.floor(design.canvasHeight) : MAP_SIZE;
  return Math.max(1, Math.min(MAP_SIZE, Math.max(width, height)));
};

export const scaleCenteredTileBetweenMaps = (
  tile: number,
  sourceMapSize: number,
  targetMapSize = MAP_SIZE,
): number => {
  const safeSourceMapSize = Math.max(1, Math.floor(sourceMapSize));
  const safeTile = clampTile(tile, safeSourceMapSize);
  const scaledCenter = ((safeTile + 0.5) * targetMapSize) / safeSourceMapSize - 0.5;
  return clampTile(Math.round(scaledCenter), targetMapSize);
};

export const scaleEdgeTileBetweenMaps = (
  tile: number,
  sourceMapSize: number,
  targetMapSize = MAP_SIZE,
): number => {
  const safeSourceMapSize = Math.max(1, Math.floor(sourceMapSize));
  const safeTile = clampTile(tile, safeSourceMapSize);
  const scaledEdge = (safeTile * targetMapSize) / safeSourceMapSize;
  return clampTile(Math.round(scaledEdge), targetMapSize);
};

export const resolveZoneAtTile = (
  scene: Pick<SceneDocument, 'zones' | 'defaultZone'>,
  x: number,
  y: number,
): string => {
  const zone = scene.zones.find(
    (entry) =>
      x >= entry.minTileX && x <= entry.maxTileX && y >= entry.minTileY && y <= entry.maxTileY,
  );
  return zone?.id ?? scene.defaultZone;
};

export const markerStyle = (
  tileX: number,
  tileY: number,
  layout: WorldLayout,
): { left: string; top: string } => ({
  left: `${((tileX + 0.5) / layout.tileWidth) * 100}%`,
  top: `${((tileY + 0.5) / layout.tileHeight) * 100}%`,
});

export const sceneToComposerState = (
  scene: SceneDocument,
): {
  name: string;
  description: string;
  tags: string[];
  status: SceneStatus;
  levelDesignId: string;
  backgroundUrl: string | null;
  foregroundUrl: string | null;
  traversalMaskUrl: string | null;
  thumbnailUrl: string | null;
  animatedGifs: SceneDocument['animatedGifs'];
  worldLayout: SceneDocument['worldLayout'];
  defaultCameraZoom: number;
  playerSpawnTile: { x: number; y: number };
  defaultZone: string;
  zones: SceneDocument['zones'];
  signs: SceneDocument['signs'];
  doors: SceneDocument['doors'];
  annotations: SceneDocument['annotations'];
  npcPlacements: SceneDocument['npcPlacements'];
  characterScale: SceneDocument['characterScale'];
  characterScaleMultiplier: SceneDocument['characterScaleMultiplier'];
  ambientZoneProfile: SceneDocument['ambientZoneProfile'];
} => ({
  name: scene.name,
  description: scene.description,
  tags: scene.tags ?? [],
  status: scene.status === 'featured' ? 'published' : scene.status,
  levelDesignId: scene.levelDesignId,
  backgroundUrl: scene.backgroundUrl,
  foregroundUrl: scene.foregroundUrl ?? null,
  traversalMaskUrl: scene.traversalMaskUrl,
  thumbnailUrl: scene.thumbnailUrl,
  animatedGifs: scene.animatedGifs ?? [],
  worldLayout: normalizeWorldLayout(scene.worldLayout),
  defaultCameraZoom: normalizeCameraZoom(scene.defaultCameraZoom),
  playerSpawnTile: clampTilePoint(scene.playerSpawnTile, normalizeWorldLayout(scene.worldLayout)),
  defaultZone: scene.defaultZone,
  zones: scene.zones,
  signs: scene.signs,
  doors: scene.doors,
  annotations: normalizeWorldAnnotations(
    scene.annotations,
    normalizeWorldLayout(scene.worldLayout),
  ),
  npcPlacements: scene.npcPlacements.map((placement) =>
    cloneSceneNpcPlacement(placement, normalizeWorldLayout(scene.worldLayout)),
  ),
  characterScale: normalizeCharacterScale(scene.characterScale),
  characterScaleMultiplier: normalizeCharacterScaleMultiplier(scene.characterScaleMultiplier),
  ambientZoneProfile: scene.ambientZoneProfile,
});

export const cloneTemplateToDraft = (scene: SceneDocument) => ({
  ...sceneToComposerState(scene),
  name: `${scene.name} Copy`,
  description: scene.description,
  tags: (scene.tags ?? []).filter((tag) => tag !== 'system'),
  npcPlacements: [],
  status: 'draft' as const,
});

export const buildSceneComposerExportScene = ({
  draft,
  initialScene,
  ownerDisplayName,
  sceneId,
  userId,
}: {
  draft: SceneCompositionInput;
  initialScene: SceneDocument | null;
  ownerDisplayName: string;
  sceneId: string | null;
  userId: string;
}): SceneDocument | null => {
  if (!sceneId) {
    return null;
  }

  return {
    ...(initialScene ?? {
      id: sceneId,
      ownerId: userId,
      ownerDisplayName,
      visitCount: 0,
      createdAt: null,
      updatedAt: null,
      publishedAt: null,
    }),
    ...draft,
    id: sceneId,
    ownerId: userId,
    ownerDisplayName,
    visitCount: initialScene?.visitCount ?? 0,
    createdAt: initialScene?.createdAt ?? null,
    updatedAt: initialScene?.updatedAt ?? null,
    publishedAt: initialScene?.publishedAt ?? null,
  };
};

export function cloneSceneNpcPlacement(
  placement: SceneNpcPlacement,
  layout: WorldLayout = DEFAULT_WORLD_LAYOUT,
): SceneNpcPlacement {
  return {
    ...placement,
    inlineConfig: placement.inlineConfig
      ? {
          ...placement.inlineConfig,
          ...(placement.inlineConfig.avatarLayers
            ? { avatarLayers: { ...placement.inlineConfig.avatarLayers } }
            : {}),
          ...(placement.inlineConfig.spriteColors
            ? { spriteColors: { ...placement.inlineConfig.spriteColors } }
            : {}),
          fallbackProviders: cloneFallbackProviders(placement.inlineConfig.fallbackProviders),
          personalitySpec: placement.inlineConfig.personalitySpec
            ? {
                ...placement.inlineConfig.personalitySpec,
                sliders: { ...placement.inlineConfig.personalitySpec.sliders },
                output: { ...placement.inlineConfig.personalitySpec.output },
                mbti: placement.inlineConfig.personalitySpec.mbti
                  ? { ...placement.inlineConfig.personalitySpec.mbti }
                  : null,
                enneagram: placement.inlineConfig.personalitySpec.enneagram
                  ? { ...placement.inlineConfig.personalitySpec.enneagram }
                  : null,
              }
            : null,
          agentConfig: placement.inlineConfig.agentConfig
            ? {
                ...placement.inlineConfig.agentConfig,
                permissions: [...placement.inlineConfig.agentConfig.permissions],
                enabledTools: [...placement.inlineConfig.agentConfig.enabledTools],
                enabledSkills: [...placement.inlineConfig.agentConfig.enabledSkills],
              }
            : null,
        }
      : undefined,
    movementConfig: placement.movementConfig
      ? cloneMovementConfig(
          normalizeMovementConfig(placement.movementConfig, placement.waypointPath, layout),
          layout,
        )
      : undefined,
    waypointPath: placement.waypointPath?.map((entry) => ({ ...entry })),
  };
}
